import {
  ChartBar,
  CurrencyDollar,
  Kanban,
  Printer,
  ClipboardText,
  Function as FunctionIcon,
  Megaphone,
  Package,
  Palette,
  Percent,
  Storefront,
  Stack,
  MapPin,
  Trash,
  ShoppingCart,
  ListChecks,
  PlugsConnected,
  Truck,
  Receipt,
  Drop,
  Gear,
  Question,
  Lock,
  UsersThree,
  IdentificationCard,
  BookOpen,
  UserPlus,
  List,
  X,
} from '@phosphor-icons/react';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { AdminNav } from '@/components/admin/admin-nav';
import { SystemMark } from '@/components/system-mark';
import { groupNav } from '@/lib/admin-nav';
import { RELEASE, versionLabel } from '@/lib/version';
import { prospectsDueQuery, adminShellQuery, tenantQuery } from '@/queries';

/**
 * The admin shell. The route's `beforeLoad` has already bounced anyone who is
 * not admin or staff, and the API refuses the data regardless, so by the time
 * this renders the session is known good.
 */
export function AdminLayout() {
  const { me, can, quotes } = useSuspenseQuery(adminShellQuery).data;
  // Follow-ups due today or overdue, beside the Prospects link. Spec 009 FR-920e.
  const prospectsDue = useQuery({
    ...prospectsDueQuery,
    enabled: (can.prospects ?? []).includes('view'),
  }).data?.due ?? 0;

  const queueDepth = quotes.filter(
    (q) => q.status === 'submitted' || q.status === 'proof_sent',
  ).length;

  /**
   * The navigation follows the PERMISSION MATRIX, not a role name.
   *
   * `require-perm.ts` says it plainly: a role hard-coded in TypeScript cannot
   * see a role an admin creates tomorrow, and two authorisation models drift
   * until the weaker one becomes the real one. That applied to this list too:
   * an admin could grant a manager the integrations permission on the new
   * screen and the manager would still not see the link (R-120).
   *
   * `module: null` means everybody internal: the dashboard, and the permissions
   * screen itself, which FR-809 makes readable by anyone because a person is
   * allowed to know what they may do.
   *
   * Hiding a link is a courtesy. Every route re-checks, and so does the
   * database.
   */
  /**
   * Six groups, in the order a day runs.
   *
   * Twenty items in one flat list is a list nobody reads. The order is not
   * alphabetical and not by module: it is the order work moves through the
   * business, so somebody looking for the next thing looks down rather than
   * hunting. R-214.
   *
   * TODAY first, because it is what a person opens the system to answer.
   * SELL, MAKE, STOCK, BUY follow the work: a quote becomes a job, a job eats
   * stock, stock is bought. SETTINGS last, because it is opened once a month.
   */
  /**
   * Four groups, and the names are the SHEET'S, not mine.
   *
   * I grouped these by the order a day runs (Today, Sell, Make, Stock, Buy,
   * Settings) before reading 1d properly. The sheet draws the sidebar with
   * TODAY, STOCK CONTROL, PRODUCTION, COMMERCE, and those are the words the
   * trade uses: a decoration shop says "production", not "make". Where the
   * design has an opinion it wins, because the whole point of commissioning one
   * is not to relitigate it. R-216.
   */
  // The phone and tablet menu (tracker #8). It closes when the page changes,
  // so a tap on a link lands on the new screen with the panel gone.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  // The browser tab names the system, not the shop, on every staff screen, so a
  // tab of ESSE and a tab of the shop are not two tabs called the same thing.
  // The shop's name comes back when somebody leaves for the storefront.
  const tenant = useSuspenseQuery(tenantQuery).data;
  useEffect(() => {
    const before = document.title;
    document.title = tenant.system_name;
    return () => { document.title = before; };
  }, [tenant.system_name]);
  const closeMenu = () => {
    setMenuOpen(false);
    menuButton.current?.focus();
  };

  const nav = groupNav(([
    {
      name: 'Today',
      items: [
        { href: '/admin', label: 'Dashboard', icon: ChartBar, module: null },
        // Before Quotes, because a prospect is where a quote starts. Spec 009.
        { href: '/admin/prospects', label: 'Prospects', icon: UserPlus, count: prospectsDue,
          module: 'prospects' },
        { href: '/admin/quotes', label: 'Quotes', icon: ClipboardText, count: queueDepth, module: 'quotes' },
        // Two entries, because they are two jobs. Pipeline is the work in
        // progress: five columns, a card each, and it is only a pipeline while
        // it holds work. Orders is the book: 12,656 of them, searchable, and
        // the only place an old order can be found at all.
        { href: '/admin/pipeline', label: 'Pipeline', icon: Kanban, module: 'orders' },
        { href: '/admin/orders', label: 'Orders', icon: Package, module: 'orders' },
        { href: '/admin/reorder', label: 'Reorder', icon: ShoppingCart, module: 'inventory' },
        // Next to Orders, because "who is this" and "what did they buy" are one
        // question asked twice. The module has existed since the permission
        // matrix was written and had no screen behind it at all (R-246).
        { href: '/admin/customers', label: 'Customers', icon: IdentificationCard,
          module: 'customers' },
      ],
    },
    {
      name: 'Stock control',
      items: [
        { href: '/admin/stock', label: 'Stock on hand', icon: Stack, module: 'inventory' },
        { href: '/admin/locations', label: 'Locations', icon: MapPin, module: 'inventory' },
        { href: '/admin/stock-takes', label: 'Stocktakes', icon: ClipboardText, module: 'inventory' },
        { href: '/admin/waste', label: 'Waste', icon: Trash, module: 'inventory' },
        { href: '/admin/purchase-orders', label: 'Purchase orders', icon: Receipt, module: 'purchasing' },
      ],
    },
    {
      name: 'Production',
      items: [
        { href: '/admin/jobs', label: 'Job board', icon: Kanban, module: 'jobs',
          alsoNeeds: ['machines'] },
        { href: '/admin/machines', label: 'Machines', icon: Printer, module: 'machines',
          alsoNeeds: ['jobs'] },
        // Materials before Recipes, because a recipe is written from them and
        // there was no way to create one at all until now (R-243).
        { href: '/admin/materials', label: 'Materials', icon: Drop, module: 'inventory' },
        { href: '/admin/recipes', label: 'Recipes', icon: ListChecks, module: 'materials' },
        { href: '/admin/methods', label: 'Methods', icon: FunctionIcon, module: 'pricing',
          // The screen shows who changed a rate and when, and that is the audit
          // log (R-175). Without `reports` the whole read is refused.
          alsoNeeds: ['reports'] },
      ],
    },
    {
      name: 'Commerce',
      items: [
        { href: '/admin/product-listing', label: 'Products', icon: Storefront, module: 'catalogue' },
        { href: '/admin/products', label: 'Publishing', icon: Storefront, module: 'catalogue' },
        { href: '/admin/designs', label: 'Designs', icon: Palette, module: 'catalogue' },
        { href: '/admin/suppliers', label: 'Suppliers', icon: Truck, module: 'purchasing' },
        // `integrations`, which until now was a tick in the matrix that nothing
        // read (R-248). The screen still needs the catalogue, because mapping a
        // Shopify variant to a product is the job it exists for.
        { href: '/admin/integrations', label: 'Shopify', icon: PlugsConnected,
          module: 'integrations', alsoNeeds: ['catalogue'] },
        // Beside Shopify, because "where did my invoice go" and "where did my
        // order go" are the same question asked of two different bridges.
        { href: '/admin/xero', label: 'Xero', icon: Receipt, module: 'settings' },
      ],
    },
    {
      // Spec 017, FR-1701. The reports Guy reads at the end of a month. The
      // value needs costs.view as well; without it the screen shows the
      // quantities and says the costs are not shown.
      name: 'Reports',
      items: [
        { href: '/admin/reports/stock-value', label: 'Stock value', icon: CurrencyDollar, module: 'reports' },
      ],
    },
    {
      name: 'Settings',
      items: [
        { href: '/admin/how-we-do-things', label: 'Procedures', icon: BookOpen, module: 'documents' },
        { href: '/admin/people', label: 'Staff', icon: UsersThree, module: 'users' },
        { href: '/admin/permissions', label: 'Permissions', icon: Lock, module: null },
        // Last in Settings, because it is where somebody goes when they cannot
        // find a setting anywhere else. It links out to the ones that have
        // their own screen rather than copying them.
        { href: '/admin/settings', label: 'Settings', icon: Gear, module: 'settings' },
        // Last, and reachable by anybody internal rather than gated on a
        // module: the person who most needs the manual is the one who has not
        // been given permissions yet.
        { href: '/admin/help', label: 'Help', icon: Question, module: null },
      ],
    },
  ] as const), can);

  return (
    <div className="flex min-h-[100dvh] flex-col">
      <header className="sticky top-0 z-40 border-b border-line bg-raised">
        <div className="flex h-12 items-center gap-4 px-[18px]">
          {/* Tracker #8. Below 1,024 px the sidebar is hidden, and until this
              button there was nothing in its place: a phone or an iPad in
              portrait landed on the dashboard with no way to anywhere else. */}
          <div className="flex shrink-0 items-center gap-2 lg:hidden">
            <button
              ref={menuButton}
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="admin-menu"
              className="-ml-2 grid h-11 w-11 place-items-center rounded-[6px] text-ink hover:bg-deep"
            >
              <List size={22} weight="bold" aria-hidden />
            </button>
            <SystemMark />
          </div>
          {/* Search sits in the top bar and the wordmark does not: 1d puts the
              mark at the top of the SIDEBAR, above the groups, so the top bar
              is entirely the thing a person is doing right now. R-216. */}
          <label className="relative flex-1 max-w-[560px]">
            <span className="sr-only">Search items, SKUs, locations</span>
            <input
              type="search"
              placeholder="Search items, SKUs, locations"
              className="h-8 w-full rounded-[6px] border border-line bg-panel pl-8 pr-3 text-[12.5px] text-ink placeholder:text-faint focus:border-[color:var(--color-ink)] focus:outline-2 focus:outline-offset-2 focus:outline-[color:var(--color-control)]"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-faint"
            >
              &#9675;
            </span>
          </label>

          {/* The theme toggle and the "View store" link were here. Both are
              removed for now, at the client's request. R-267.
              
              "View store" led to the storefront, which is closed, and the
              theme toggle is a preference nobody asked for on a working
              screen. The components are kept: `ThemeToggle` still serves the
              storefront, and putting either back is one line.

              Neither is deleted from the codebase, so this is a decision that
              can be reversed rather than work that has to be redone. */}
        </div>
      </header>

      <div className="flex w-full flex-1">
        {/* The sidebar carries the mark at the top and the person at the
            bottom, which is where 1d puts them. It is its own column rather
            than a child of a centred container, so it reaches the full height
            of the window and the table beside it gets the rest. */}
        <div className="flex w-[220px] shrink-0 flex-col border-r border-line bg-raised max-lg:hidden">
          <SidebarBody nav={nav} me={me} />
        </div>

        {menuOpen && (
          <MenuPanel onClose={closeMenu}>
            <SidebarBody nav={nav} me={me} roomy />
          </MenuPanel>
        )}

        <main id="main" className="min-w-0 flex-1 px-[18px] py-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

type Nav = ReturnType<typeof groupNav>;
type Me = { user?: { email?: string | null } | null; isAdmin?: boolean; isInternal?: boolean };

/**
 * What the sidebar holds: the mark, the menu and the person. Drawn by the
 * sidebar on a wide screen and by the menu panel on a narrow one, from this one
 * component, so the two cannot drift apart.
 */
function SidebarBody({ nav, me, roomy = false }: { nav: Nav; me: Me; roomy?: boolean }) {
  const { system_name } = useSuspenseQuery(tenantQuery).data;
  return (
    <>
      <div className="flex h-12 items-center gap-2 px-[18px]">
        <SystemMark />
      </div>

      {/*
        * THE NAV AND THE ACCOUNT BLOCK SCROLL TOGETHER.
        *
        * The nav was `flex-1` and the account block was pinned under it, so
        * on any screen taller than the menu there was a hand's width of
        * empty sidebar between "Help" and the person's own name. Christian:
        * "look at the distance between the account menu and Help menu, so
        * much space."
        *
        * Pinning it to the bottom is the conventional choice and it is the
        * thing making the gap, so it goes. The account block now follows
        * the last menu item, and the leftover height falls below both,
        * where it is empty sidebar rather than a hole in the middle of it.
        */}
      <div className="min-h-0 flex-1 overflow-y-auto pb-4 pt-2">
        <div className="px-2.5">
          <AdminNav roomy={roomy} groups={nav.map((g) => ({ ...g, items: g.items.map(({ icon, ...rest }) => rest) }))} />
        </div>

        {/*
          * Who is signed in, AND THE WAY TO DO SOMETHING ABOUT IT.
          *
          * It answers "am I looking at what Katherine sees?", which is the
          * question somebody asks when a column is missing. It was a plain
          * `div`, so it answered that and nothing else: Christian asked
          * "how do I get to the account menu from this admin screen?" and
          * the honest answer was that there was no way. The only route to
          * /account was to know the URL and type it.
          *
          * ONE control, not two. There was a Sign out beside it for an
          * afternoon, and Christian asked for it back out: the account
          * screen already has one, and two ways to sign out sitting a
          * centimetre apart is a decision nobody needed to be offered.
          *
          * It goes to `/admin/account`, not `/account`. The second is the
          * CUSTOMER'S screen: a default shipping address, marketing
          * preferences, and a request to delete your account. Guy has never
          * bought anything from Obsessed, and deleting his account would
          * take the studio's administrator with it.
          */}
        <div className="mt-3 border-t border-line pt-3">
          <Link
            to="/admin/account"
            className="flex items-center gap-2.5 px-[18px] py-2 hover:bg-deep"
          >
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[color:var(--color-control)] font-[family-name:var(--font-mono)] text-[10px] font-medium text-[color:var(--color-on-control)]">
              {(me.user?.email ?? '?').slice(0, 2).toUpperCase()}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[12px] font-medium">
                {me.user?.email ?? 'Signed out'}
              </span>
              <span className="block font-[family-name:var(--font-mono)] text-[9.5px] uppercase tracking-[0.08em] text-muted">
                {me.isAdmin ? 'Administrator' : me.isInternal ? 'Staff' : 'Customer'}
              </span>
            </span>
          </Link>
        </div>

        {/* The version, under a divider, below the person. Not in the menu,
            because it is not somewhere to go. The same release stamp is on
            every fault report, so the number a person reads out is the one
            the logs hold. Christian, 30 October 2026. */}
        <p
          className="mx-[18px] mt-3 border-t border-line pt-3 font-[family-name:var(--font-mono)] text-[10px] tracking-[0.04em] text-muted"
          title={RELEASE}
        >
          {system_name} {versionLabel() ? `version ${versionLabel()}` : 'development build'}
        </p>
      </div>
    </>
  );
}

/**
 * The menu on a phone or a tablet in portrait. Tracker #8.
 *
 * A panel from the left over the page, because that is where the sidebar is on
 * a wide screen and where people expect a menu. It closes on a link (the
 * layout watches the path), on Escape, on the backdrop and on its own button.
 * Focus moves into it when it opens and back to the menu button when it
 * closes, and the page behind it does not scroll.
 */
function MenuPanel({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    close.current?.focus();
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', key);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', key);
      document.body.style.overflow = overflow;
    };
    // Once per opening. `onClose` is a new function on every render.
  }, []);

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <button
        type="button"
        aria-label="Close menu"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 bg-[rgba(15,17,21,0.45)]"
      />
      <div
        id="admin-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className="absolute inset-y-0 left-0 flex w-[300px] max-w-[85vw] flex-col bg-raised shadow-xl"
        // A tap on the link of the page you are already on changes no path, so
        // the layout's own close never fires. Any link closes the panel.
        onClick={(e) => { if ((e.target as HTMLElement).closest('a')) onClose(); }}
      >
        <button
          ref={close}
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="absolute right-2 top-1.5 z-10 grid h-11 w-11 place-items-center rounded-[6px] text-ink hover:bg-deep"
        >
          <X size={20} weight="bold" aria-hidden />
        </button>
        {children}
      </div>
    </div>
  );
}

